import { useState } from "react";
import { Badge } from "../../primitives/badge";
import {
  MailOpen,
  Send,
  CalendarDays,
  MessageSquare,
  CheckSquare,
  MapPin,
  Users,
  Clock,
  ArrowDownLeft,
  ArrowUpRight,
  ExternalLink,
} from "lucide-react";
import { isToday, isTomorrow, isPast, isFuture } from "date-fns";
import type {
  CommunicationItem,
  EmailInteraction,
  CalendarInteraction,
} from "@shared-core/mock-data/communications";
import {
  formatDateOrDash,
  formatLongWeekdayDateWithYearOrDash,
  formatTimeOrDash,
} from "@shared-core/domain/formatters";
import { LinkEmailToJobDialog } from "./LinkEmailToJobDialog";

interface CommunicationTimelineProps {
  items: CommunicationItem[];
  className?: string;
}

export function CommunicationTimeline({
  items,
  className,
}: CommunicationTimelineProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // "Link to a job…" (CSR command-centre manual-link work, J4 mirror) —
  // set when a CSR opens the picker for one email row; see
  // LinkEmailToJobDialog's file header for why the write lives in the
  // dialog itself rather than being reconciled back into `items` here.
  const [linkingEmail, setLinkingEmail] = useState<EmailInteraction | null>(
    null,
  );

  return (
    <div className={className}>
      {/* Timeline */}
      <div className="space-y-0">
        {items.map((item) => {
          const id = getItemId(item);
          const isExpanded = expandedId === id;
          return (
            <CommunicationEntry
              key={id}
              item={item}
              isExpanded={isExpanded}
              onToggle={() => setExpandedId(isExpanded ? null : id)}
              onLinkToJob={setLinkingEmail}
            />
          );
        })}
      </div>

      {linkingEmail && (
        <LinkEmailToJobDialog
          open={linkingEmail !== null}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setLinkingEmail(null);
          }}
          customerId={linkingEmail.customer_id}
          email={linkingEmail}
        />
      )}
    </div>
  );
}

function getItemId(item: CommunicationItem): string {
  return item.data.id;
}

function isUnreadEmail(item: CommunicationItem): boolean {
  return item.type === "email" && item.data.is_read === false;
}

function getItemDate(item: CommunicationItem): string {
  switch (item.type) {
    case "email":
      return item.data.sent_at;
    case "meeting":
      return item.data.start_at;
    case "note":
      return item.data.created_at;
    case "ticket":
      return item.data.created_at;
  }
}

// --- Individual entry renderer ---

interface CommunicationEntryProps {
  item: CommunicationItem;
  isExpanded: boolean;
  onToggle: () => void;
  /** Opens the "Link to a job…" picker for this row — email rows only. */
  onLinkToJob: (email: EmailInteraction) => void;
}

function CommunicationEntry({
  item,
  isExpanded,
  onToggle,
  onLinkToJob,
}: CommunicationEntryProps) {
  const date = new Date(getItemDate(item));
  const formattedDate = formatDateOrDash(date);
  const formattedTime = formatTimeOrDash(date);

  return (
    <div className="relative flex gap-4 pb-1">
      {/* Timeline connector */}
      <div className="absolute left-4 top-10 bottom-0 w-px bg-border" />

      {/* Icon */}
      <div
        className={`relative z-10 flex items-center justify-center size-8 rounded-full bg-background border-2 shrink-0 ${getIconColor(item)}`}
      >
        {getIcon(item)}
      </div>

      {/* Content */}
      <div
        className="group flex-1 border border-border rounded-lg p-3 mb-3 hover:bg-muted/30 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              {getTypeBadge(item)}
              {getDirectionBadge(item)}
              {getMeetingTimeBadge(item)}
            </div>
            <h4
              className={`text-sm truncate flex items-center gap-1.5 ${
                isUnreadEmail(item) ? "font-semibold" : "font-medium"
              }`}
            >
              <span className="truncate">{getTitle(item)}</span>
              {isUnreadEmail(item) && (
                <span
                  className="inline-block size-1.5 rounded-full bg-blue-600 shrink-0"
                  aria-label="Unread"
                />
              )}
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              {getSubtitle(item)}
            </p>
          </div>
          <div className="text-xs text-muted-foreground text-right shrink-0 flex flex-col items-end gap-1">
            <div>{formattedDate}</div>
            <div>{formattedTime}</div>
            {item.type === "email" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onLinkToJob(item.data);
                }}
                className="text-xs text-[color:var(--color-primary)] hover:underline opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
              >
                Link to a job…
              </button>
            )}
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="mt-3 pt-3 border-t border-border">
            {renderExpandedContent(item)}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Visual configuration ---

function getIcon(item: CommunicationItem) {
  switch (item.type) {
    case "email":
      return item.data.direction === "inbound" ? (
        <MailOpen className="size-4" />
      ) : (
        <Send className="size-4" />
      );
    case "meeting":
      return <CalendarDays className="size-4" />;
    case "note":
      return <MessageSquare className="size-4" />;
    case "ticket":
      return <CheckSquare className="size-4" />;
  }
}

function getIconColor(item: CommunicationItem): string {
  switch (item.type) {
    case "email":
      return item.data.direction === "inbound"
        ? "text-blue-600 border-blue-200"
        : "text-sky-600 border-sky-200";
    case "meeting": {
      const meetingDate = new Date(item.data.start_at);
      if (isFuture(meetingDate)) return "text-violet-600 border-violet-200";
      return "text-purple-600 border-purple-200";
    }
    case "note":
      return "text-amber-600 border-amber-200";
    case "ticket":
      return item.data.status === "completed"
        ? "text-green-600 border-green-200"
        : "text-orange-600 border-orange-200";
  }
}

function getTypeBadge(item: CommunicationItem) {
  const variants: Record<string, { label: string; className: string }> = {
    email_in: {
      label: "Email received",
      className: "bg-blue-50 text-blue-700 border-blue-200",
    },
    email_out: {
      label: "Email sent",
      className: "bg-sky-50 text-sky-700 border-sky-200",
    },
    meeting_past: {
      label: "Meeting",
      className: "bg-purple-50 text-purple-700 border-purple-200",
    },
    meeting_future: {
      label: "Upcoming meeting",
      className: "bg-violet-50 text-violet-700 border-violet-200",
    },
    note: {
      label: "Note",
      className: "bg-amber-50 text-amber-700 border-amber-200",
    },
    ticket_open: {
      label: "Ticket",
      className: "bg-orange-50 text-orange-700 border-orange-200",
    },
    ticket_done: {
      label: "Ticket completed",
      className: "bg-green-50 text-green-700 border-green-200",
    },
  };

  let key: string;
  switch (item.type) {
    case "email":
      key = item.data.direction === "inbound" ? "email_in" : "email_out";
      break;
    case "meeting":
      key = isFuture(new Date(item.data.start_at))
        ? "meeting_future"
        : "meeting_past";
      break;
    case "note":
      key = "note";
      break;
    case "ticket":
      key = item.data.status === "completed" ? "ticket_done" : "ticket_open";
      break;
  }

  const v = variants[key];
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${v.className}`}
    >
      {v.label}
    </span>
  );
}

function getDirectionBadge(item: CommunicationItem) {
  if (item.type !== "email") return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
      {item.data.direction === "inbound" ? (
        <>
          <ArrowDownLeft className="size-3" />
          from {item.data.from_name}
        </>
      ) : (
        <>
          <ArrowUpRight className="size-3" />
          to {item.data.contact_name}
        </>
      )}
    </span>
  );
}

function getMeetingTimeBadge(item: CommunicationItem) {
  if (item.type !== "meeting") return null;
  const start = new Date(item.data.start_at);
  if (isToday(start)) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
        <Clock className="size-3" />
        Today
      </span>
    );
  }
  if (isTomorrow(start)) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
        <Clock className="size-3" />
        Tomorrow
      </span>
    );
  }
  return null;
}

function getTitle(item: CommunicationItem): string {
  switch (item.type) {
    case "email":
      return item.data.subject;
    case "meeting":
      return item.data.title;
    case "note":
      return item.data.note_text.length > 80
        ? item.data.note_text.slice(0, 80) + "..."
        : item.data.note_text;
    case "ticket":
      return item.data.title;
  }
}

function getSubtitle(item: CommunicationItem): string {
  switch (item.type) {
    case "email":
      return `${item.data.team_member_name} · ${item.data.snippet.slice(0, 100)}...`;
    case "meeting":
      return `${item.data.team_member_name} · ${item.data.attendees.map((a) => a.name).join(", ")}`;
    case "note":
      return `Added by team`;
    case "ticket":
      return item.data.due_date
        ? `Due ${formatDateOrDash(item.data.due_date)}`
        : "No due date";
  }
}

// --- Expanded content ---

function renderExpandedContent(item: CommunicationItem) {
  switch (item.type) {
    case "email":
      return <EmailExpanded email={item.data} />;
    case "meeting":
      return <MeetingExpanded meeting={item.data} />;
    case "note":
      return <NoteExpanded note={item.data} />;
    case "ticket":
      return <TicketExpanded ticket={item.data} />;
  }
}

function EmailExpanded({ email }: { email: EmailInteraction }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">From:</span>
        <span>
          {email.from_name} &lt;{email.from_address}&gt;
        </span>
        <span className="text-muted-foreground">To:</span>
        <span>{email.to_addresses.join(", ")}</span>
        <span className="text-muted-foreground">Subject:</span>
        <span className="font-medium">{email.subject}</span>
      </div>
      <div className="text-sm text-muted-foreground bg-muted/50 rounded p-3 mt-2">
        {email.snippet}
      </div>
      <button className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1">
        <ExternalLink className="size-3" />
        Open in Outlook
      </button>
    </div>
  );
}

function MeetingExpanded({ meeting }: { meeting: CalendarInteraction }) {
  const start = new Date(meeting.start_at);
  const end = new Date(meeting.end_at);
  const duration = Math.round((end.getTime() - start.getTime()) / 60000);
  const isPastMeeting = isPast(end);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">When:</span>
        <span>
          {formatLongWeekdayDateWithYearOrDash(start)} ·{" "}
          {formatTimeOrDash(start)} – {formatTimeOrDash(end)} ({duration} min)
        </span>
        {meeting.location && (
          <>
            <span className="text-muted-foreground">Where:</span>
            <span className="inline-flex items-center gap-1">
              <MapPin className="size-3" />
              {meeting.location}
            </span>
          </>
        )}
        <span className="text-muted-foreground">Attendees:</span>
        <span className="inline-flex items-center gap-1">
          <Users className="size-3" />
          {meeting.attendees.map((a) => a.name).join(", ")}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Badge variant={isPastMeeting ? "secondary" : "default"}>
          {isPastMeeting ? "Completed" : meeting.status}
        </Badge>
        <button className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
          <ExternalLink className="size-3" />
          Open in Outlook
        </button>
      </div>
    </div>
  );
}

function NoteExpanded({
  note,
}: {
  note: { id: string; note_text: string; created_at: string; user_id: string };
}) {
  return (
    <div className="text-sm text-muted-foreground bg-muted/50 rounded p-3">
      {note.note_text}
    </div>
  );
}

function TicketExpanded({
  ticket,
}: {
  ticket: {
    id: string;
    title: string;
    status: string;
    created_at: string;
    resolved_at: string | null;
    due_date: string | null;
  };
}) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">Status:</span>
        <span>
          <Badge
            variant={
              ticket.status === "resolved" || ticket.status === "closed"
                ? "secondary"
                : "default"
            }
          >
            {ticket.status}
          </Badge>
        </span>
        <span className="text-muted-foreground">Created:</span>
        <span>{formatDateOrDash(ticket.created_at)}</span>
        {ticket.due_date && (
          <>
            <span className="text-muted-foreground">Due:</span>
            <span>{formatDateOrDash(ticket.due_date)}</span>
          </>
        )}
        {ticket.resolved_at && (
          <>
            <span className="text-muted-foreground">Resolved:</span>
            <span>{formatDateOrDash(ticket.resolved_at)}</span>
          </>
        )}
      </div>
    </div>
  );
}
